/**
 * 项目管理 API
 *
 * 提供项目元数据的查询与管理能力：列表查询、选择器数据、启停操作、编辑配置、手动同步
 */
import apiClient from './index'

// ============== 类型定义 ==============

export interface ProjectItem {
  id: string
  project_id: string
  project_name: string | null
  database_name: string | null
  industry: string | null
  description: string | null
  settings: Record<string, unknown>
  is_active: boolean
  is_offline: boolean
  created_at: string | null
  updated_at: string | null
  created_by: string | null
}

export interface ProjectSelectorItem {
  project_id: string
  project_name: string | null
  is_active: boolean
  company_name: string | null
  /** 由后端从 project.settings.logo_url 透传；前端不再硬编码路径推导。 */
  logo_url: string | null
}

export interface ProjectListResponse {
  items: ProjectItem[]
  total: number
  page: number
  page_size: number
}

export interface ProjectSelectorResponse {
  items: ProjectSelectorItem[]
}

export interface ProjectListParams {
  is_active?: boolean
  is_offline?: boolean
  keyword?: string
  page?: number
  page_size?: number
}

export interface UpdateProjectRequest {
  project_name?: string
  database_name?: string
  industry?: string
  description?: string
  settings?: Record<string, unknown>
}

export type ProjectCloneScope =
  | 'all'
  | 'metrics'
  | 'sql_examples'
  | 'alert_rules'
  | 'report_templates'
  | 'instructions'
  | 'subject_tree'

export interface CloneScopeStats {
  cloned: number
  skipped: number
  failed: number
}

export interface CloneProjectConfigRequest {
  target_project_id: string
  scope: ProjectCloneScope[]
}

export interface CloneProjectConfigResponse {
  source_project_id: string
  target_project_id: string
  scope: ProjectCloneScope[]
  metrics: CloneScopeStats
  sql_examples: CloneScopeStats
  alert_rules: CloneScopeStats
  report_templates: CloneScopeStats
  instructions: CloneScopeStats
  subject_tree: CloneScopeStats
  total_cloned: number
  total_skipped: number
  total_failed: number
}

// ============== API 方法 ==============

/**
 * 查询项目列表（运维管理页，分页）
 */
export async function fetchProjects(params?: ProjectListParams): Promise<ProjectListResponse> {
  return apiClient.get<ProjectListResponse>('/projects', { params })
}

/**
 * 获取项目选择器数据（仅返回未下线的项目）
 */
export async function fetchProjectSelector(): Promise<ProjectSelectorResponse> {
  return apiClient.get<ProjectSelectorResponse>('/projects/selector')
}

/**
 * 切换项目启用状态
 */
export async function toggleProjectActive(
  projectId: string,
  isActive: boolean,
): Promise<{ message: string; is_active: boolean }> {
  return apiClient.put<{ message: string; is_active: boolean }>(
    `/projects/${projectId}/active`,
    { is_active: isActive },
  )
}

/**
 * 更新项目信息（名称/品类/settings 配置）
 */
export async function updateProject(
  projectId: string,
  data: UpdateProjectRequest,
): Promise<{ message: string }> {
  return apiClient.put<{ message: string }>(`/projects/${projectId}`, data)
}

/**
 * 克隆项目治理配置
 */
export async function cloneProjectConfig(
  projectId: string,
  data: CloneProjectConfigRequest,
): Promise<CloneProjectConfigResponse> {
  return apiClient.post<CloneProjectConfigResponse>(`/projects/${projectId}/clone`, data)
}

/**
 * 手动触发望舒项目同步
 */
export async function triggerProjectSync(): Promise<{ message: string; success: boolean }> {
  return apiClient.post<{ message: string; success: boolean }>('/projects/sync', undefined, {
    timeout: 180000,
  })
}
